import React, { useCallback, useId, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';

const DEFAULT_OFFSET = 12;

function getTooltipCoords(anchor, placement, offset) {
  const rect = anchor.getBoundingClientRect();
  switch (placement) {
    case 'left':
      return {
        top: rect.top + rect.height / 2,
        left: rect.left - offset,
        transform: 'translate(-100%, -50%)',
      };
    case 'bottom':
      return {
        top: rect.bottom + offset,
        left: rect.left + rect.width / 2,
        transform: 'translate(-50%, 0)',
      };
    case 'top':
      return {
        top: rect.top - offset,
        left: rect.left + rect.width / 2,
        transform: 'translate(-50%, -100%)',
      };
    case 'right':
    default:
      return {
        top: rect.top + rect.height / 2,
        left: rect.right + offset,
        transform: 'translateY(-50%)',
      };
  }
}

/**
 * Design-system nav tooltip — fixed portal so it clears rail overflow and main content.
 * Pair with `useNavTooltip` on any nav row/control that needs a hover/focus label.
 */
export function NavTooltip({
  show,
  anchorRef,
  id,
  label,
  placement = 'right',
  offset = DEFAULT_OFFSET,
}) {
  const [coords, setCoords] = useState(null);

  const updateCoords = useCallback(() => {
    const anchor = anchorRef?.current;
    if (!anchor) return;
    setCoords(getTooltipCoords(anchor, placement, offset));
  }, [anchorRef, placement, offset]);

  useLayoutEffect(() => {
    if (!show) {
      setCoords(null);
      return undefined;
    }
    updateCoords();
    window.addEventListener('scroll', updateCoords, true);
    window.addEventListener('resize', updateCoords);
    return () => {
      window.removeEventListener('scroll', updateCoords, true);
      window.removeEventListener('resize', updateCoords);
    };
  }, [show, updateCoords]);

  if (!show || !coords || !label || typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <div
      id={id}
      role="tooltip"
      className="y-nav-tooltip"
      style={{
        top: coords.top,
        left: coords.left,
        transform: coords.transform,
      }}
    >
      {label}
    </div>,
    document.body
  );
}

/**
 * @param {string} label Full tooltip text (also used for aria-label when provided).
 * @returns {{ triggerProps: object, tooltip: React.ReactNode, tooltipId: string, show: boolean }}
 */
export function useNavTooltip(label) {
  const anchorRef = React.useRef(null);
  const tooltipId = useId();
  const [show, setShow] = useState(false);

  const showTooltip = useCallback(() => setShow(true), []);
  const hideTooltip = useCallback(() => setShow(false), []);

  const triggerProps = {
    ref: anchorRef,
    onMouseEnter: showTooltip,
    onMouseLeave: hideTooltip,
    onFocus: showTooltip,
    onBlur: hideTooltip,
    'aria-describedby': show ? tooltipId : undefined,
    ...(label ? { 'aria-label': label } : {}),
  };

  const tooltip = (
    <NavTooltip show={show} anchorRef={anchorRef} id={tooltipId} label={label} />
  );

  return { anchorRef, tooltipId, show, triggerProps, tooltip };
}
